import React from "react";
import type { ApolloClient } from "@apollo/client";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { debounce, get, groupBy, includes, isEmpty, map, sortBy } from "lodash";
import {
  getLastWorkflowStepForWorkflow,
  getPaginatedWorkflows,
} from "actions/adminActions";
import { createErrorSelector, createLoadingSelector } from "store/selectors";
import { isSuperIconAdmin } from "store/selectors/admin";
import { Col, Form, Row } from "react-bootstrap";
import { adminConstants } from "actions/types";
import moment from "moment";
import Alert from "components/Alert";
import Paginator from "components/Paginator";
import IconTable, { type IconTableColumn } from "components/IconTable";
import Button from "components/Button";
import CopyIcon from "components/CopyIcon";
import IconSpinner from "components/IconSpinner";
import IconTableHeader from "components/IconTableHeader";
import { createDateFilterRange } from "utils/timeHelper";
import queryString from "query-string";
import IconMessageCard from "components/IconMessageCard";
import { FiDollarSign } from "react-icons/fi";
import WorkflowStepModal from "./WorkflowStepModal";
import RestartWorkflowModal from "./RestartWorkflowModal";
import { push } from "store/actions/router";
import WorkflowParamsModal from "pages/dashboards/adminDashboard/workflows/WorkflowParamsModal";
import { Link } from "react-router-dom";
import type { RootState } from "types/state";

/** A workflow row as rendered in the table — shaped from the fields read here. */
interface WorkflowRow {
  id?: string;
  entityId?: string;
  entityType?: string;
  description?: string;
  name?: string;
  status?: string;
  pausedUntil?: string;
  updatedAt?: string;
  workflow?: { params?: Record<string, unknown> };
}

const isLoadingSelector = createLoadingSelector(adminConstants.GET_WORKFLOWS);
const errorSelector = createErrorSelector(adminConstants.GET_WORKFLOWS);

const mapStateToProps = (state: RootState) => {
  const workflowStepsById = groupBy(state.admin.workflowSteps, "id");
  const workflowResults = get(state.admin, "workflows.results", []);
  const filterValues = get(state.admin, "workflows.filterValues", {});
  return {
    workflowStepsById,
    getLastWorkflowStep: getLastWorkflowStepForWorkflow,
    isFetching: isLoadingSelector(state),
    workflows: workflowResults as WorkflowRow[],
    workflowFilterValues: filterValues as { name?: string[] },
    workflowsCount: get(state.admin, "workflows.totalRows", 0),
    error: errorSelector(state),
    isSuperAdmin: isSuperIconAdmin(state),
  };
};

const mapDispatchToProps = {
  getPaginatedWorkflows,
  push,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = WithApolloClient<PropsFromRedux>;

interface WorkflowFilters {
  startDate: Date;
  endDate: Date;
  entityId: string | null;
  status: string | null;
  name: string | null;
  id: string | null;
  // written only by _handleOrderFilterChange
  order?: string;
}

interface State {
  initialFetching: boolean;
  page: number;
  limit: number;
  offset: number;
  selectedDaysAgoFilter: string | number;
  filters: WorkflowFilters;
  showLastWorkflowStepModal: boolean;
  showWorkflowParamsModal: boolean;
  showRestartWorkflowModal: boolean;
  activeWorkflow: WorkflowRow;
}

class Workflows extends React.PureComponent<Props, State> {
  _workflowStatuses = [
    // null value returns all workflows
    {
      label: "All",
      value: "ALL",
    },
    {
      label: "Complete",
      value: "COMPLETE",
    },
    {
      label: "Exit",
      value: "EXIT",
    },
    {
      label: "Failed",
      value: "FAILED",
    },
    {
      label: "Locked",
      value: "LOCKED",
    },
    {
      label: "New",
      value: "NEW",
    },
    {
      label: "Paused",
      value: "PAUSED",
    },
    {
      label: "Running",
      value: "RUNNING",
    },
  ];
  _dateFilters = [
    {
      label: "Past 1 day",
      value: 1,
    },
    {
      label: "Past 3 days",
      value: 3,
    },
    {
      label: "Past 7 days",
      value: 7,
    },
    {
      label: "Past 30 days",
      value: 30,
    },
    {
      label: "Past 60 days",
      value: 60,
    },
  ];

  constructor(props: Props) {
    super(props);
    const queryParams = queryString.parse(window.location.search);
    const defaultDaysAgoFilter = (queryParams.selectedDaysAgoFilter || 1) as
      | string
      | number;
    const { startDate, endDate } = createDateFilterRange(
      defaultDaysAgoFilter as unknown as number
    );

    let filters: WorkflowFilters = {
      startDate,
      endDate,
      entityId: (queryParams.entityId as string) || null,
      status: (queryParams.status as string) || "FAILED",
      name: (queryParams.name as string) || null,
      id: (queryParams.id as string) || null,
    };

    this.state = {
      initialFetching: true,
      page: 0,
      limit: 25,
      offset: 0,
      selectedDaysAgoFilter: defaultDaysAgoFilter,
      filters,
      showLastWorkflowStepModal: false,
      showWorkflowParamsModal: false,
      showRestartWorkflowModal: false,
      activeWorkflow: {},
    };
  }

  componentDidMount() {
    void this._getPaginatedWorkflows().then(() => {
      this.setState({ initialFetching: false });
    });
  }

  _onPageChange = ({ selected }: { selected: number }) => {
    const offset = selected * this.state.limit;

    this.setState({ page: selected, offset }, () =>
      this._getPaginatedWorkflows()
    );
  };

  _getPaginatedWorkflows = () => {
    const queryParams = {
      selectedDaysAgoFilter: this.state.selectedDaysAgoFilter,
      entityId: this.state.filters.entityId,
      status: this.state.filters.status,
      name: this.state.filters.name,
      id: this.state.filters.id,
    };
    // preserve filters in url so refreshes don't cause loss of data and can share links
    const newQueryString = queryString.stringify(queryParams);
    const newLocation = `${window.location.pathname}?${newQueryString}`;
    window.history.pushState({}, "", newLocation);

    return this.props.getPaginatedWorkflows(
      this.props.client as ApolloClient<object>,
      {
        limit: this.state.limit,
        offset: this.state.offset,
        // found-not-fixed: startDate/endDate are Date objects from
        // createDateFilterRange, but updatedAtStart/End are typed `string` in
        // GetPaginatedWorkflowsQueryVariables; status is `string | null` here
        // vs the `WorkflowStatus` enum. Preserve the runtime coercion as-is.
        updatedAtStart: this.state.filters.startDate,
        updatedAtEnd: this.state.filters.endDate,
        status: this.state.filters.status,
        entityId: this.state.filters.entityId,
        name: this.state.filters.name,
        id: this.state.filters.id,
      } as unknown as Parameters<typeof getPaginatedWorkflows>[1]
    );
  };

  _buildWorkflowColumns = (): Array<IconTableColumn<WorkflowRow>> => {
    const columns: Array<IconTableColumn<WorkflowRow>> = [
      {
        label: "Workflow ID",
        key: "id",
        customComponent: (props) => (
          <>
            {props.id}
            <CopyIcon copyText={props.id} label="Workflow ID" />
          </>
        ),
      },
      {
        label: "Subject Id",
        key: "entityId",
        customComponent: (props) => {
          let url;
          if (props.entityType == "User") {
            url = `/users/${props.entityId}`;
          } else if (props.entityType == "Employer") {
            url = `/employers/${props.entityId}`;
          } else {
            return (
              <>
                {props.entityId}
                <CopyIcon copyText={props.entityId} label="Subject ID" />
              </>
            );
          }
          return (
            <>
              <Link to={url} className="icon-link">
                {props.entityId}
              </Link>
              <CopyIcon copyText={props.entityId} label="Subject ID" />
            </>
          );
        },
      },
      {
        label: "Subject Type",
        key: "entityType",
      },
      {
        label: "Description",
        key: "description",
      },
      {
        label: "Name",
        key: "name",
        customComponent: (props) => (
          <>
            {props.name}
            <CopyIcon copyText={props.name} label="Workflow name" />
          </>
        ),
      },
      {
        label: "Status",
        key: "status",
      },
      {
        label: "Updated At",
        customComponent: (props) => {
          const commonDateFormat = "MMM Do YYYY";
          return <>{moment(props.updatedAt).format(commonDateFormat)}</>;
        },
      },

      {
        label: "Paused Until",
        key: "pausedUntil",
      },
      {
        key: "actions",
        customComponent: (props) => {
          const params = get(props, "workflow.params", {});
          return (
            <div className={"btn-row"}>
              {!isEmpty(params) && (
                // @ts-expect-error Button types `name` as required; it is
                // optional in practice (omitted here in the original JS).
                // Pre-existing inert prop omission preserved for behavior parity.
                <Button
                  color="primary"
                  btnLabel="Params"
                  onClick={() => {
                    this.setState({
                      showWorkflowParamsModal: true,
                      activeWorkflow: props,
                    });
                  }}
                />
              )}
              {
                // @ts-expect-error Button types `name` as required; it is
                // optional in practice (omitted here in the original JS).
                // Pre-existing inert prop omission preserved for behavior parity.
                <Button
                  color="action"
                  btnLabel="Last Step"
                  onClick={() => {
                    this.setState({
                      showLastWorkflowStepModal: true,
                      activeWorkflow: props,
                    });
                  }}
                />
              }
              {this.props.isSuperAdmin &&
                props.status === "FAILED" && (
                  // @ts-expect-error Button types `name` as required; it is
                  // optional in practice (omitted here in the original JS).
                  <Button
                    color="primary"
                    btnLabel="Restart"
                    onClick={() => {
                      this.setState({
                        showRestartWorkflowModal: true,
                        activeWorkflow: props,
                      });
                    }}
                  />
                )}
            </div>
          );
        },
      },
    ];
    return columns;
  };

  _handleStatusFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    let status: string | null = e.target.value;
    if (status === "ALL") {
      status = null;
    }
    this.setState(
      {
        filters: { ...this.state.filters, status },
        offset: 0,
        page: 0,
      },
      () => this._getPaginatedWorkflows()
    );
  };

  _handleNameFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    let name: string | null = e.target.value;
    if (name === "ALL") {
      name = null;
    }

    this.setState(
      {
        filters: { ...this.state.filters, name },
        offset: 0,
        page: 0,
      },
      () => this._getPaginatedWorkflows()
    );
  };

  _handleDaysAgoFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    let daysAgo = e.target.value;
    // found-not-fixed: daysAgo is a string from the select; createDateFilterRange
    // expects a number — preserve the runtime coercion moment() performs.
    const { startDate, endDate } = createDateFilterRange(
      daysAgo as unknown as number
    );
    this.setState(
      {
        filters: { ...this.state.filters, startDate, endDate },
        selectedDaysAgoFilter: daysAgo,
        offset: 0,
        page: 0,
      },
      () => this._getPaginatedWorkflows()
    );
  };

  _handleOrderFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    this.setState(
      {
        filters: { ...this.state.filters, order: e.target.value },
        offset: 0,
        page: 0,
      },
      () => this._getPaginatedWorkflows()
    );
  };

  _handleEntityIdFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState(
      {
        filters: { ...this.state.filters, [e.target.name]: e.target.value },
        offset: 0,
        page: 0,
      },
      () => this._getPaginatedWorkflows()
    );
  };

  _debounceOnChange = debounce(this._handleEntityIdFilterChange, 500);

  _getQueryParamWorkfowName = () => {
    const queryParams = queryString.parse(window.location.search);

    if (!isEmpty(queryParams.name)) {
      const filterValueAlreadyExists = includes(
        this.props.workflowFilterValues.name,
        queryParams.name
      );

      if (filterValueAlreadyExists) return null;

      // query-string types name as `string | string[] | null`; the !isEmpty
      // guard guarantees a non-empty string at runtime when this branch runs.
      const name = queryParams.name as string;
      return (
        <option key={name} value={name}>
          {name}
        </option>
      );
    }

    return null;
  };

  render() {
    if (this.state.initialFetching) {
      return <IconSpinner centered />;
    }

    if (this.props.error) {
      return <Alert type={"error"} msg={this.props.error} />;
    }

    const columns = this._buildWorkflowColumns();
    const data = this.props.workflows;

    const showBlankSlate = isEmpty(data);

    return (
      <div className="mega-container">
        <section className="page-title-wrap">
          <article className="text-cell">
            <h1 className="page-title">Workflows</h1>
            <div className="main-content">
              <>
                {this.props.isFetching && <IconSpinner centered />}
                {this.state.showLastWorkflowStepModal && (
                  <WorkflowStepModal
                    // @ts-expect-error WorkflowStepModal has no `show` prop —
                    // it hardcodes show={true} on its inner SimpleModal; this
                    // attribute is a no-op kept for behavior parity
                    // (found-not-fixed: extraneous prop).
                    show={this.state.showLastWorkflowStepModal}
                    workflowId={this.state.activeWorkflow.id}
                    onClose={() => {
                      this.setState({
                        showLastWorkflowStepModal: false,
                        activeWorkflow: {},
                      });
                    }}
                  />
                )}
                {this.state.showRestartWorkflowModal && (
                  <RestartWorkflowModal
                    workflow={this.state.activeWorkflow}
                    onClose={() => {
                      this.setState({
                        showRestartWorkflowModal: false,
                        activeWorkflow: {},
                      });
                    }}
                    onSuccess={() => {
                      this.setState(
                        {
                          showRestartWorkflowModal: false,
                          activeWorkflow: {},
                        },
                        () => this._getPaginatedWorkflows()
                      );
                    }}
                  />
                )}
                {this.state.showWorkflowParamsModal && (
                  <WorkflowParamsModal
                    // @ts-expect-error WorkflowParamsModal has no `show` prop —
                    // it hardcodes show={true}; this attribute is a no-op kept
                    // for behavior parity (found-not-fixed: extraneous prop).
                    show={this.state.showLastWorkflowStepModal}
                    workflowParams={
                      get(
                        this.state.activeWorkflow,
                        "workflow.params",
                        {}
                      ) as Record<string, unknown>
                    }
                    onClose={() => {
                      this.setState({
                        showWorkflowParamsModal: false,
                        activeWorkflow: {},
                      });
                    }}
                  />
                )}
                <>
                  <div className="action-box">
                    <Row style={{ padding: 10 }}>
                      <Col md={4}>
                        <div className="state-filter">
                          <Form.Label>Filter by status</Form.Label>
                          <Form.Select
                            name="statusFilter"
                            value={
                              this.state.filters.status as string | undefined
                            }
                            onChange={this._handleStatusFilterChange}
                          >
                            {map(this._workflowStatuses, (filter) => (
                              <option key={filter.label} value={filter.value}>
                                {filter.label}
                              </option>
                            ))}
                          </Form.Select>
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="state-filter">
                          <Form.Label>Filter by workflow name</Form.Label>
                          <Form.Select
                            name="statusFilter"
                            value={
                              this.state.filters.name as string | undefined
                            }
                            onChange={this._handleNameFilterChange}
                          >
                            <option key={"All"} value={"ALL"}>
                              All
                            </option>
                            {this._getQueryParamWorkfowName()}
                            {map(
                              sortBy(this.props.workflowFilterValues.name),
                              (filter) => (
                                <option key={filter} value={filter}>
                                  {filter}
                                </option>
                              )
                            )}
                          </Form.Select>
                        </div>
                      </Col>
                      <Col md={3}>
                        <div className="state-filter">
                          <Form.Label>Filter by time</Form.Label>
                          <Form.Select
                            name="statusFilter"
                            value={this.state.selectedDaysAgoFilter}
                            onChange={this._handleDaysAgoFilterChange}
                          >
                            {map(this._dateFilters, (filter) => (
                              <option key={filter.label} value={filter.value}>
                                {filter.label}
                              </option>
                            ))}
                          </Form.Select>
                        </div>
                      </Col>
                    </Row>
                    <Row style={{ padding: 10 }}>
                      <Col md={6}>
                        <div className="state-filter">
                          <Form.Label>Filter by User/Company ID</Form.Label>
                          <Form.Control
                            name="entityId"
                            value={
                              this.state.filters.entityId as string | undefined
                            }
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>
                            ) => {
                              this.setState({
                                filters: {
                                  ...this.state.filters,
                                  entityId: e.target.value,
                                },
                              });
                              this._debounceOnChange(e);
                            }}
                          ></Form.Control>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="state-filter">
                          <Form.Label>Filter by Workflow ID</Form.Label>
                          <Form.Control
                            name="id"
                            value={this.state.filters.id as string | undefined}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>
                            ) => {
                              this.setState({
                                filters: {
                                  ...this.state.filters,
                                  id: e.target.value,
                                },
                              });
                              this._debounceOnChange(e);
                            }}
                          ></Form.Control>
                        </div>
                      </Col>
                    </Row>
                    <IconTableHeader
                      tableHeader="Workflows"
                      tableCount={this.props.workflowsCount}
                    />
                    {showBlankSlate && (
                      <div>
                        <IconMessageCard
                          header={"No workflows"}
                          subheader="There are currently no workflows to display."
                          icon={
                            <FiDollarSign
                              color="white"
                              stroke="#60A4BF"
                              size={16}
                            />
                          }
                        />
                      </div>
                    )}
                    {!showBlankSlate && (
                      <>
                        <IconTable columns={columns} data={data} />
                        <Paginator
                          onChange={this._onPageChange}
                          pageCount={
                            this.props.workflowsCount / this.state.limit
                          }
                          page={this.state.page}
                        />
                      </>
                    )}
                  </div>
                </>
              </>
            </div>
          </article>
        </section>
      </div>
    );
  }
}

export default connector(withApollo<Props>(Workflows));
